import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lightning,
  ChartLineUp,
  HandCoins,
  Lock,
  CheckCircle,
  UserPlus,
  DeviceMobile,
  ChatCircleDots,
  List,
  X,
  Plus,
  Minus,
  SealCheck,
  SignIn,
  Calculator,
  Coins,
  Percent,
  Receipt,
  Star,
  Quotes,
  TrendUp,
  Clock,
} from "@phosphor-icons/react";
import InstallAppButton from "../components/InstallAppButton";
import CMFFooter from "../components/CMFFooter";
import { simulate } from "../lib/loanMath";
import { formatCLP } from "../lib/format";

/* Tasa de interés fija del simulador: 1,5% mensual */
const RATE_MENSUAL = 1.5;
const MONTO_MIN = 100000;
const MONTO_MAX = 20000000;
const MONTO_STEP = 50000;
const CUOTA_OPCIONES = [6, 12, 18, 24, 36, 48];

/* ====== Marca UNIK VENTURES (colores del logo) ====== */
const BRAND = {
  green: "#035938",
  greenDeep: "#002C1A",
  greenNight: "#011A10",
  gold: "#BB8C2A",
  goldLight: "#F1CE68",
};
const GOLD_GRADIENT = `linear-gradient(135deg, ${BRAND.goldLight} 0%, #D9AE4A 45%, ${BRAND.gold} 100%)`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1758599543129-bec77e427ac6?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80";

const BENEFITS = [
  { icon: HandCoins, title: "Cuenta sin costo", desc: "Abre tu cuenta digital en minutos, sin mantención ni letra chica. Tú controlas tu dinero." },
  { icon: ChartLineUp, title: "Créditos a tu medida", desc: "Simula y solicita créditos desde la app, con respuesta rápida de tu asesor." },
  { icon: Lightning, title: "Transferencias con token", desc: "Mueve tu dinero de forma segura con confirmación por código de 4 dígitos." },
  { icon: ChatCircleDots, title: "Asesor dedicado", desc: "Conversa por chat con un asesor real que acompaña cada decisión financiera." },
  { icon: DeviceMobile, title: "100% digital", desc: "Opera desde tu teléfono o computador. Instálala como app y llévala contigo." },
  { icon: Lock, title: "Seguridad de banco", desc: "Acceso con RUT y PIN, sesiones cifradas y datos protegidos de punta a punta." },
];

const STEPS = [
  { n: "01", icon: UserPlus, title: "Regístrate con tu RUT", desc: "Ingresa tus datos básicos. Validamos tu RUT chileno al instante." },
  { n: "02", icon: Lock, title: "Crea tu PIN seguro", desc: "Define un PIN de 4 dígitos para proteger tu acceso. Listo en segundos." },
  { n: "03", icon: Calculator, title: "Simula y solicita", desc: "Elige monto y cuotas. Tu asesor revisa la solicitud y te responde por chat." },
  { n: "04", icon: Coins, title: "Recibe tu dinero", desc: "Con tu crédito aprobado, el desembolso llega directo a tu cuenta UNIK." },
];

const TESTIMONIALS = [
  {
    name: "Matías González",
    role: "Emprendedor · Santiago",
    img: "https://images.unsplash.com/photo-1656399910161-8efafd973a29?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop&q=80",
    quote: "Pedí capital de trabajo para mi negocio y en el mismo día mi asesor me respondió por chat. Todo claro, sin letra chica.",
  },
  {
    name: "Valentina Rojas",
    role: "Diseñadora independiente · Viña del Mar",
    img: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop&q=80",
    quote: "El simulador me mostró la cuota exacta antes de registrarme. Abrí mi cuenta desde el celular en menos de cinco minutos.",
  },
  {
    name: "Sebastián Muñoz",
    role: "Ingeniero · Concepción",
    img: "https://images.unsplash.com/photo-1757744705465-ea08b0ddc38a?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop&q=80",
    quote: "Me da tranquilidad que cada transferencia se confirme con token. Se siente tan seguro como un banco, pero mucho más simple.",
  },
];

const FAQS = [
  { q: "¿Cuánto cuesta abrir una cuenta UNIK VENTURES?", a: "Nada. Abrir tu cuenta es totalmente gratuito y sin costos de mantención. Solo necesitas tu RUT chileno y un correo." },
  { q: "¿Qué necesito para registrarme?", a: "Tu RUT chileno válido, un número de teléfono, un correo electrónico y definir un PIN de 4 dígitos. Todo el proceso es en línea." },
  { q: "¿Cómo se calcula la cuota del crédito?", a: "Usamos el sistema de amortización francés con una tasa referencial de 1,5% mensual. El monto, tasa y plazo finales quedan sujetos a la evaluación de tu asesor." },
  { q: "¿Es seguro operar en la plataforma?", a: "Sí. Usamos acceso con RUT y PIN, sesiones cifradas y confirmación por token en las transferencias. Tus datos están protegidos de extremo a extremo." },
  { q: "¿UNIK VENTURES está regulada?", a: "Sí. UNIK VENTURES S.A. (RUT 77.894.847-8) se encuentra inscrita y vigente en el registro de la Comisión para el Mercado Financiero (CMF), categoría RAFIP · Mercado O, desde el 15/07/2024." },
  { q: "¿Puedo hablar con una persona real?", a: "Claro. Cada cliente cuenta con un asesor dedicado con quien puede chatear directamente desde la app para resolver dudas y recibir orientación." },
];

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("animate-fade-up");
            e.target.style.opacity = "";
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function Eyebrow({ children, dark = false }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] mb-4"
      style={{ color: dark ? BRAND.goldLight : BRAND.gold }}
    >
      <span className="w-6 h-px" style={{ background: dark ? BRAND.goldLight : BRAND.gold }} />
      {children}
    </div>
  );
}

function GoldButton({ to, children, testid, className = "" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2 h-14 px-7 font-display font-bold tracking-wide text-[#002C1A] shadow-[0_10px_30px_-10px_rgba(241,206,104,0.55)] hover:brightness-105 transition-all ${className}`}
      style={{ background: GOLD_GRADIENT }}
    >
      {children}
      <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

export default function LandingUnik() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [monto, setMonto] = useState(3000000);
  const [cuotas, setCuotas] = useState(24);
  useReveal();

  const sim = useMemo(() => simulate(monto, RATE_MENSUAL, cuotas), [monto, cuotas]);
  const pct = ((monto - MONTO_MIN) / (MONTO_MAX - MONTO_MIN)) * 100;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#beneficios", label: "Beneficios" },
    { href: "#simulador", label: "Simulador" },
    { href: "#como-funciona", label: "Cómo funciona" },
    { href: "#testimonios", label: "Testimonios" },
    { href: "#preguntas", label: "Preguntas" },
  ];

  return (
    <div className="min-h-screen bg-bg text-ink font-body overflow-x-hidden" data-testid="landing-unik">
      {/* ===== NAV ===== */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl border-b border-white/10 shadow-lg" : "bg-transparent"
        }`}
        style={scrolled ? { background: "rgba(0,44,26,0.92)" } : undefined}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <span className="w-10 h-10 bg-white flex items-center justify-center shadow-md">
              <img src="/logo-unik.png" alt="UNIK VENTURES" className="w-8 h-8 object-contain" />
            </span>
            <div className="leading-none text-white">
              <div className="font-display font-black text-lg tracking-[0.08em]">UNIK</div>
              <div className="font-mono text-[9px] tracking-[0.42em] mt-1" style={{ color: BRAND.goldLight }}>
                VENTURES
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium text-white/75 hover:text-white transition-colors">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 h-10 px-4 text-sm font-semibold text-white/90 hover:text-white transition-colors"
              data-testid="nav-login"
            >
              <SignIn size={16} weight="bold" /> Ingresar
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 h-10 px-5 text-sm font-bold text-[#002C1A] hover:brightness-105 transition-all"
              style={{ background: GOLD_GRADIENT }}
              data-testid="nav-register"
            >
              Abre tu cuenta <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <button
            className="lg:hidden p-2 -mr-2 text-white"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menú"
            data-testid="nav-menu-toggle"
          >
            {menuOpen ? <X size={26} /> : <List size={26} />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-white/10 px-5 py-4 space-y-1" style={{ background: BRAND.greenDeep }} data-testid="nav-mobile-menu">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="block py-3 text-base font-medium text-white/90 border-b border-white/5">
                {l.label}
              </a>
            ))}
            <div className="flex gap-3 pt-4">
              <Link to="/login" className="flex-1 inline-flex items-center justify-center gap-1.5 h-12 text-sm font-semibold border border-white/30 text-white">
                <SignIn size={16} weight="bold" /> Ingresar
              </Link>
              <Link to="/register" className="flex-1 inline-flex items-center justify-center gap-2 h-12 text-sm font-bold text-[#002C1A]" style={{ background: GOLD_GRADIENT }}>
                Abre tu cuenta
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section
        className="relative text-white pt-[72px] overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${BRAND.greenDeep} 0%, ${BRAND.greenNight} 55%, #000F09 100%)` }}
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(800px 420px at 85% 10%, ${BRAND.green}AA, transparent 65%), radial-gradient(600px 380px at 5% 90%, ${BRAND.gold}22, transparent 60%)`,
          }}
        />
        {/* fine grid */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-7" data-reveal>
            <div className="inline-flex items-center gap-2 h-8 px-3 border border-white/15 bg-white/5 text-[11px] font-mono uppercase tracking-[0.2em] mb-7" style={{ color: BRAND.goldLight }}>
              <SealCheck size={14} weight="fill" /> Inscrita en CMF Chile
            </div>
            <h1 className="font-display text-[2.7rem] leading-[0.98] sm:text-6xl lg:text-[4.6rem] font-black tracking-tighter mb-6" data-testid="hero-title">
              Impulsa tus planes
              <br />
              con un crédito{" "}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: GOLD_GRADIENT }}>
                a tu medida.
              </span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-9 leading-relaxed">
              En UNIK VENTURES abres tu cuenta 100% en línea, simulas y solicitas tu crédito, transfieres con
              seguridad y conversas con tu asesor. Sin costo y sin filas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <GoldButton to="/register" testid="hero-register">Abre tu cuenta gratis</GoldButton>
              <a
                href="#simulador"
                className="inline-flex items-center justify-center gap-2 h-14 px-7 border border-white/25 text-white font-display font-semibold tracking-wide hover:bg-white/10 transition-colors"
                data-testid="hero-simulate"
              >
                <Calculator size={18} weight="bold" /> Simular mi crédito
              </a>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
              {["Sin costo de apertura", "Respuesta en 24 h", "Datos cifrados"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle size={18} weight="fill" style={{ color: BRAND.goldLight }} /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative" data-reveal>
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-4 border" style={{ borderColor: `${BRAND.goldLight}40` }} />
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={HERO_IMG} alt="Clienta de UNIK VENTURES usando su cuenta digital" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${BRAND.greenNight}CC 100%)` }} />
              </div>

              <div className="absolute -left-4 sm:-left-10 top-10 bg-white text-ink shadow-2xl px-4 py-3 flex items-center gap-3" data-testid="hero-card-approved">
                <div className="w-10 h-10 flex items-center justify-center text-white" style={{ background: BRAND.green }}>
                  <CheckCircle size={20} weight="fill" />
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Crédito</div>
                  <div className="font-display font-bold text-sm">Aprobado · {formatCLP(3000000)}</div>
                </div>
              </div>

              <div className="absolute -right-3 sm:-right-8 bottom-12 bg-white text-ink shadow-2xl p-4 w-52" data-testid="hero-card-cuota">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Tu cuota</div>
                  <TrendUp size={16} weight="bold" style={{ color: BRAND.gold }} />
                </div>
                <div className="font-display text-2xl font-black" style={{ color: BRAND.green }}>
                  {formatCLP(Math.round(simulate(3000000, RATE_MENSUAL, 24).monthly))}
                </div>
                <div className="text-xs text-inkMuted mt-0.5">24 cuotas · 1,5% mensual</div>
                <div className="mt-3 h-1.5 bg-black/5 overflow-hidden">
                  <div className="h-full w-2/3" style={{ background: GOLD_GRADIENT }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* stats strip */}
        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
            {[
              { k: "$20M", v: "Monto máximo de crédito" },
              { k: "48", v: "Cuotas como máximo" },
              { k: "24 h", v: "Respuesta de tu asesor" },
              { k: "$0", v: "Costo de mantención" },
            ].map((s, i) => (
              <div key={s.v} className={`py-7 px-2 sm:px-6 ${i % 2 === 1 ? "border-l border-white/10" : ""} ${i >= 2 ? "border-t lg:border-t-0 border-white/10" : ""} ${i === 2 ? "lg:border-l" : ""}`}>
                <div className="font-display text-3xl sm:text-4xl font-black" style={{ color: BRAND.goldLight }}>{s.k}</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFICIOS ===== */}
      <section id="beneficios" className="scroll-mt-20 py-20 lg:py-28" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-14" data-reveal>
            <div className="lg:col-span-7">
              <Eyebrow>Por qué UNIK VENTURES</Eyebrow>
              <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
                Finanzas simples,
                <br />
                <span style={{ color: BRAND.green }}>respaldo de verdad.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 lg:pt-10 text-lg text-inkMuted leading-relaxed">
              Todo lo que necesitas para gestionar tu dinero y financiar tus proyectos, en una sola plataforma
              diseñada para Chile.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border border-hairline">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="group bg-surface p-8 lg:p-10 hover:bg-[#F3F7F4] transition-colors" data-reveal data-testid={`benefit-${i}`}>
                <div className="w-12 h-12 flex items-center justify-center mb-6 text-white transition-transform group-hover:-translate-y-1" style={{ background: i % 3 === 1 ? GOLD_GRADIENT : BRAND.green }}>
                  <b.icon size={24} weight="duotone" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">{b.title}</h3>
                <p className="text-inkMuted leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SIMULADOR ===== */}
      <section
        id="simulador"
        className="scroll-mt-20 relative text-white overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BRAND.greenDeep} 0%, ${BRAND.greenNight} 100%)` }}
        data-testid="simulator-section"
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(700px 360px at 100% 0%, ${BRAND.gold}26, transparent 60%)` }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <div className="max-w-2xl mb-12" data-reveal>
            <Eyebrow dark>Simulador de crédito</Eyebrow>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mb-4">Calcula tu cuota en segundos</h2>
            <p className="text-lg text-white/65">
              Mueve el monto y el plazo para estimar tu crédito con una tasa de{" "}
              <span className="font-semibold text-white">{String(RATE_MENSUAL).replace(".", ",")}% mensual</span>. Sin compromiso y antes de registrarte.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-px bg-white/10 border border-white/15" data-reveal>
            <div className="lg:col-span-3 bg-surface text-ink p-7 sm:p-10">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
                <label htmlFor="sim-monto" className="label-mono flex items-center gap-2">
                  <Coins size={14} weight="duotone" /> Monto del crédito
                </label>
                <div className="font-display text-3xl sm:text-4xl font-black" style={{ color: BRAND.green }} data-testid="sim-monto-value">
                  {formatCLP(monto)}
                </div>
              </div>
              <input
                id="sim-monto"
                type="range"
                min={MONTO_MIN}
                max={MONTO_MAX}
                step={MONTO_STEP}
                value={monto}
                onChange={(e) => setMonto(Number(e.target.value))}
                className="sim-range w-full"
                style={{ background: `linear-gradient(90deg, ${BRAND.green} ${pct}%, rgba(11,26,18,0.1) ${pct}%)` }}
                data-testid="sim-monto"
              />
              <div className="flex justify-between text-xs text-inkMuted font-mono mt-2">
                <span>{formatCLP(MONTO_MIN)}</span>
                <span>{formatCLP(MONTO_MAX)}</span>
              </div>

              <div className="mt-10">
                <label className="label-mono flex items-center gap-2 mb-3">
                  <Receipt size={14} weight="duotone" /> Número de cuotas
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {CUOTA_OPCIONES.map((c) => {
                    const active = cuotas === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCuotas(c)}
                        data-testid={`sim-cuota-${c}`}
                        className={`h-12 font-display font-bold text-sm transition-colors border ${
                          active ? "bg-forest text-white border-forest" : "bg-surface text-ink border-hairline hover:border-forest"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 flex items-start gap-2 text-sm text-inkMuted">
                <Percent size={16} weight="duotone" className="text-burnt shrink-0 mt-0.5" />
                Tasa referencial {String(RATE_MENSUAL).replace(".", ",")}% mensual · sistema de amortización francés.
              </div>
            </div>

            <div className="lg:col-span-2 p-7 sm:p-10 flex flex-col" style={{ background: "rgba(3,89,56,0.55)" }}>
              <div className="label-mono mb-1" style={{ color: BRAND.goldLight }}>Cuota mensual estimada</div>
              <div className="font-display text-4xl sm:text-5xl font-black tracking-tight mb-6" data-testid="sim-cuota-mensual">
                {formatCLP(Math.round(sim.monthly))}
              </div>
              <dl className="border-t border-white/15">
                {[
                  ["Monto solicitado", formatCLP(monto)],
                  ["Plazo", `${cuotas} cuotas`],
                  ["Total intereses", formatCLP(Math.round(sim.interest))],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-3.5 border-b border-white/15">
                    <dt className="text-white/65 text-sm">{k}</dt>
                    <dd className="font-mono font-semibold">{v}</dd>
                  </div>
                ))}
                <div className="flex items-center justify-between py-3.5">
                  <dt className="text-white/65 text-sm">Total a pagar</dt>
                  <dd className="font-mono font-bold" style={{ color: BRAND.goldLight }} data-testid="sim-total">
                    {formatCLP(Math.round(sim.total))}
                  </dd>
                </div>
              </dl>
              <GoldButton to="/register" testid="sim-cta" className="mt-6 w-full">Solicita este crédito</GoldButton>
              <p className="text-white/45 text-[11px] leading-relaxed mt-3">
                Valores referenciales. El monto, tasa y plazo finales quedan sujetos a evaluación de tu asesor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CÓMO FUNCIONA ===== */}
      <section id="como-funciona" className="scroll-mt-20 py-20 lg:py-28" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14" data-reveal>
            <Eyebrow>Cómo funciona</Eyebrow>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
              De la idea al dinero <span style={{ color: BRAND.green }}>en 4 pasos.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-surface border border-hairline p-7 hover:shadow-xl hover:-translate-y-1 transition-all" data-reveal data-testid={`step-${i}`}>
                <div className="flex items-center justify-between mb-8">
                  <div className="w-12 h-12 flex items-center justify-center text-white" style={{ background: BRAND.green }}>
                    <s.icon size={22} weight="duotone" />
                  </div>
                  <div className="font-display text-5xl font-black bg-clip-text text-transparent" style={{ backgroundImage: GOLD_GRADIENT }}>
                    {s.n}
                  </div>
                </div>
                <h3 className="font-display text-lg font-bold mb-2">{s.title}</h3>
                <p className="text-inkMuted text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIOS ===== */}
      <section id="testimonios" className="scroll-mt-20 py-20 lg:py-28 bg-surface border-y border-hairline" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14" data-reveal>
            <div>
              <Eyebrow>Testimonios</Eyebrow>
              <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
                Clientes que ya
                <br />
                <span style={{ color: BRAND.green }}>crecen con nosotros.</span>
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex" style={{ color: BRAND.gold }}>
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={20} weight="fill" />)}
              </div>
              <div className="text-sm text-inkMuted"><span className="font-bold text-ink">4,9/5</span> valoración promedio</div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                className={`relative p-8 flex flex-col border ${i === 1 ? "text-white border-transparent" : "bg-bg border-hairline"}`}
                style={i === 1 ? { background: BRAND.greenDeep } : undefined}
                data-reveal
                data-testid={`testimonial-${i}`}
              >
                <Quotes size={36} weight="fill" style={{ color: i === 1 ? BRAND.goldLight : BRAND.gold }} className="mb-5" />
                <blockquote className={`text-lg leading-relaxed mb-8 ${i === 1 ? "text-white/90" : "text-ink"}`}>“{t.quote}”</blockquote>
                <figcaption className="mt-auto flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-12 h-12 object-cover rounded-full" style={{ boxShadow: `0 0 0 2px ${BRAND.goldLight}` }} />
                  <div>
                    <div className="font-display font-bold">{t.name}</div>
                    <div className={`text-xs ${i === 1 ? "text-white/60" : "text-inkMuted"}`}>{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="preguntas" className="scroll-mt-20 py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4" data-reveal>
            <Eyebrow>Preguntas frecuentes</Eyebrow>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02] mb-5">
              ¿Tienes <span style={{ color: BRAND.green }}>dudas?</span>
            </h2>
            <p className="text-inkMuted leading-relaxed mb-6">
              Si no encuentras tu respuesta, tu asesor te ayuda por chat apenas abras tu cuenta.
            </p>
            <div className="inline-flex items-center gap-2 text-sm text-inkMuted">
              <Clock size={18} weight="duotone" style={{ color: BRAND.gold }} /> Atención de lunes a viernes, 9:00 a 18:00
            </div>
          </div>
          <div className="lg:col-span-8 border-t border-hairline">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className="border-b border-hairline">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 py-6 text-left"
                    aria-expanded={open}
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-display text-lg sm:text-xl font-bold">{f.q}</span>
                    <span
                      className="w-9 h-9 shrink-0 flex items-center justify-center border transition-colors"
                      style={open ? { background: BRAND.green, borderColor: BRAND.green, color: "#fff" } : { borderColor: "rgba(11,26,18,0.15)" }}
                    >
                      {open ? <Minus size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
                    </span>
                  </button>
                  {open && (
                    <p className="pb-6 pr-14 text-inkMuted leading-relaxed animate-fade-up" data-testid={`faq-answer-${i}`}>{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="px-5 sm:px-8 pb-20 lg:pb-28" data-testid="final-cta">
        <div
          className="relative max-w-7xl mx-auto overflow-hidden text-white px-7 py-14 sm:px-14 sm:py-20"
          style={{ background: `linear-gradient(135deg, ${BRAND.green} 0%, ${BRAND.greenDeep} 60%, ${BRAND.greenNight} 100%)` }}
          data-reveal
        >
          <img src="/logo-unik.png" alt="" aria-hidden="true" className="absolute -right-16 -bottom-16 w-80 h-80 object-contain opacity-[0.12] pointer-events-none" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] mb-5" style={{ color: BRAND.goldLight }}>
              <ShieldCheck size={16} weight="duotone" /> Seguro · Simple · Chileno
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02] mb-5">
              Abre tu cuenta UNIK VENTURES hoy.
            </h2>
            <p className="text-lg text-white/70 mb-9">
              Regístrate gratis en minutos, simula tu crédito y conversa con tu asesor. Instala la app y tenla siempre a mano.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <GoldButton to="/register" testid="cta-register">Crear mi cuenta</GoldButton>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 h-14 px-7 border border-white/25 text-white font-display font-semibold hover:bg-white/10 transition-colors"
                data-testid="cta-login"
              >
                <SignIn size={18} weight="bold" /> Ya tengo cuenta
              </Link>
              <InstallAppButton variant="primary" />
            </div>
          </div>
        </div>
      </section>

      <CMFFooter variant="light" />
    </div>
  );
}
